import { useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { PhoneCall, Upload } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { apiClient } from '@/lib/api'
import { explainError } from '@/lib/explain-error'
import { formatDateTime } from '@/lib/utils'
import { useUsers } from '@/hooks/use-api'
import { useToast } from '@/hooks/use-toast'
import type { CallAccountChoice, CallImportPreview, CallImportResult } from '@/types'

/** Picker values that aren't user ids. */
const NO_USER = '__none__'
const NOT_A_REP = '__not_a_rep__'

function talk(seconds: number) {
  const m = Math.round(seconds / 60)
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`
}

function choiceFor(account: string, value: string): CallAccountChoice {
  if (value === NOT_A_REP) return { external_account: account, user_id: null, not_a_rep: true }
  if (value === NO_USER) return { external_account: account, user_id: null }
  return { external_account: account, user_id: value }
}

/** Picks who a phone-system sub-account belongs to: a GRM user, "not a rep", or not set. */
function AccountPicker({
  id,
  account,
  value,
  onChange,
  users,
}: {
  id: string
  account: string
  value: string
  onChange: (v: string) => void
  users: { id: string; name: string }[]
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        id={id}
        className="w-64"
        aria-label={`Who phone account ${account} belongs to`}
      >
        <SelectValue placeholder="Choose a user" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NO_USER}>Not set</SelectItem>
        <SelectItem value={NOT_A_REP}>Not a rep (e.g. a shared line)</SelectItem>
        {users.map((u) => (
          <SelectItem key={u.id} value={u.id}>
            {u.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

function resultText(r: CallImportResult) {
  const parts = [
    `Imported ${r.added} call${r.added === 1 ? '' : 's'}${r.skipped ? `, skipped ${r.skipped} already in GRM` : ''}.`,
    `${r.matched} linked to a contact, lead or account${
      r.unmatched + r.ambiguous
        ? `; ${r.unmatched + r.ambiguous} not matched (see the Not matched tab)`
        : ''
    }.`,
  ]
  if (r.relinked)
    parts.push(
      `${r.relinked} earlier unmatched call${r.relinked === 1 ? ' is' : 's are'} now matched too.`
    )
  if (r.unmappedAccounts?.length)
    parts.push(
      `Calls from ${r.unmappedAccounts.length} phone account${r.unmappedAccounts.length === 1 ? '' : 's'} came in without a rep: say who ${r.unmappedAccounts.length === 1 ? 'it belongs' : 'they belong'} to under Phone accounts and GRM will update them.`
    )
  return parts.join(' ')
}

export default function CallImport() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<CallImportPreview | null>(null)
  const [result, setResult] = useState<CallImportResult | null>(null)
  const [choices, setChoices] = useState<Record<string, string>>({})

  const { data: usersData } = useUsers({ limit: 10000 })
  const users = useMemo(
    () =>
      (usersData?.data ?? [])
        .filter((u) => u.is_active !== false)
        .map((u) => ({ id: u.id, name: u.name || u.email || u.id }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [usersData]
  )

  const accountsQuery = useQuery({
    queryKey: ['call-import', 'accounts'],
    queryFn: () => apiClient.getCallAccounts(),
  })
  const unmatchedQuery = useQuery({
    queryKey: ['call-import', 'unmatched'],
    queryFn: () => apiClient.getUnmatchedCalls(1),
  })

  const previewMutation = useMutation({
    mutationFn: (f: File) => apiClient.callImportPreview(f),
    onSuccess: (p) => {
      setPreview(p)
      setResult(null)
      setChoices(Object.fromEntries(p.unmappedAccounts.map((a) => [a, NO_USER])))
    },
    onError: (e) =>
      toast({
        title: "The file couldn't be read",
        description: explainError(e, 'Check it is a VoIP.ms call export and try again.'),
        variant: 'destructive',
      }),
  })

  const saveAccounts = useMutation({
    mutationFn: (mappings: CallAccountChoice[]) => apiClient.saveCallAccounts(mappings),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: ['call-import', 'accounts'] })
      toast({
        title: 'Phone accounts saved',
        description: r.updatedCalls
          ? `${r.updatedCalls} imported call${r.updatedCalls === 1 ? '' : 's'} updated to match.`
          : undefined,
      })
      if (file) previewMutation.mutate(file)
    },
    onError: (e) =>
      toast({
        title: "The phone accounts weren't saved",
        description: explainError(e, 'Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const commitMutation = useMutation({
    mutationFn: (f: File) => apiClient.callImportCommit(f),
    onSuccess: (r) => {
      setResult(r)
      setPreview(null)
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
      queryClient.invalidateQueries({ queryKey: ['call-import'] })
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
    },
    onError: (e) =>
      toast({
        title: "The calls weren't imported",
        description: explainError(e, 'Nothing was saved. Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const rematchMutation = useMutation({
    mutationFn: () => apiClient.rematchCalls(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['call-import', 'unmatched'] })
      toast({
        title: r.relinked
          ? `${r.relinked} call${r.relinked === 1 ? '' : 's'} matched`
          : 'No new matches',
        description: r.relinked
          ? undefined
          : 'Add the number to the right contact, lead or account, then try again.',
      })
    },
    onError: (e) =>
      toast({
        title: "The calls couldn't be matched again",
        description: explainError(e, 'Try again in a minute.'),
        variant: 'destructive',
      }),
  })

  const unmapped = preview?.unmappedAccounts ?? []
  const anyChosen = unmapped.some((a) => choices[a] && choices[a] !== NO_USER)
  const unmatchedTotal = unmatchedQuery.data?.total ?? 0
  const unmappedSeen = (accountsQuery.data ?? []).filter((a) => !a.mapped).length
  const busy = previewMutation.isPending || commitMutation.isPending

  return (
    <main className="space-y-6" aria-labelledby="call-import-title">
      <div>
        <div className="flex items-center gap-3">
          <PhoneCall className="h-7 w-7 text-primary" aria-hidden="true" />
          <h1 id="call-import-title" className="text-3xl font-bold tracking-tight">
            Call import
          </h1>
        </div>
        <p className="text-muted-foreground mt-1 max-w-3xl">
          Bring calls from the phone system (VoIP.ms) into GRM, made and received. Each call is
          linked to the contact, lead or account with that phone number, and counts as activity
          everywhere in GRM. Importing the same calls again adds nothing.
        </p>
      </div>

      <Tabs defaultValue="import">
        <TabsList>
          <TabsTrigger value="import">Import</TabsTrigger>
          <TabsTrigger value="unmatched">
            Not matched{unmatchedTotal ? ` (${unmatchedTotal})` : ''}
          </TabsTrigger>
          <TabsTrigger value="accounts">
            Phone accounts{unmappedSeen ? ` (${unmappedSeen} to set)` : ''}
          </TabsTrigger>
        </TabsList>

        {/* ---------------- Import ---------------- */}
        <TabsContent value="import" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload a call export</CardTitle>
              <CardDescription>
                In the VoIP.ms portal, open the Call Detail Records (CDR), choose the dates with all
                sub-accounts included, and export as CSV. Upload it here unchanged. Overlapping date
                ranges are fine: calls already in GRM are skipped. Times are read as UTC−5, which is
                how VoIP.ms writes them.
              </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label htmlFor="call-file">Call export (.csv)</Label>
                <input
                  id="call-file"
                  ref={fileRef}
                  type="file"
                  accept=".csv,.txt,text/csv"
                  className="block text-sm file:mr-3 file:rounded-md file:border file:border-input file:bg-background file:px-3 file:py-1.5 file:text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null)
                    setPreview(null)
                    setResult(null)
                  }}
                />
              </div>
              <Button onClick={() => file && previewMutation.mutate(file)} disabled={!file || busy}>
                <Upload className="mr-2 h-4 w-4" aria-hidden="true" />
                {previewMutation.isPending ? 'Checking…' : 'Check file'}
              </Button>
            </CardContent>
          </Card>

          <div role="status" aria-live="polite">
            {result && (
              <p className="rounded-md border bg-muted/40 p-3 text-sm">{resultText(result)}</p>
            )}
          </div>

          {preview && file && (
            <Card>
              <CardHeader>
                <CardTitle>What will be imported</CardTitle>
                <CardDescription>
                  {preview.totals.calls} calls
                  {preview.from && preview.to
                    ? ` from ${preview.from.slice(0, 10)} to ${preview.to.slice(0, 10)}`
                    : ''}
                  . {preview.totals.new} new
                  {preview.totals.duplicates
                    ? `, ${preview.totals.duplicates} already in GRM and will be skipped`
                    : ''}
                  . Of the new calls, {preview.totals.matched} match a contact, lead or account,{' '}
                  {preview.totals.ambiguous} match several and {preview.totals.unmatched} match
                  nothing (still imported, and listed under Not matched).{' '}
                  {preview.totals.inbound
                    ? `${preview.totals.inbound} are incoming, of which ${preview.totals.missedInbound} reached no rep: those are kept on the record but don't count as anyone's activity.`
                    : ''}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">Phone account</TableHead>
                        <TableHead scope="col">Belongs to</TableHead>
                        <TableHead scope="col" className="text-right">
                          Calls
                        </TableHead>
                        <TableHead scope="col" className="text-right">
                          New
                        </TableHead>
                        <TableHead scope="col" className="text-right">
                          Answered
                        </TableHead>
                        <TableHead scope="col" className="text-right">
                          Talk time
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[...preview.reps]
                        .sort((a, b) => b.calls - a.calls)
                        .map((r) => (
                          <TableRow key={r.account ?? '(main)'}>
                            <TableCell className="font-mono text-sm">
                              {r.account ?? (
                                <span className="font-sans text-muted-foreground">
                                  Main line, no rep answered
                                </span>
                              )}
                            </TableCell>
                            <TableCell>
                              {r.account === null ? (
                                '–'
                              ) : unmapped.includes(r.account) ? (
                                <AccountPicker
                                  id={`pick-${r.account}`}
                                  account={r.account}
                                  value={choices[r.account] ?? NO_USER}
                                  onChange={(v) =>
                                    setChoices((c) => ({ ...c, [r.account as string]: v }))
                                  }
                                  users={users}
                                />
                              ) : (
                                (r.userName ?? 'Not a rep')
                              )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">{r.calls}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.new}</TableCell>
                            <TableCell className="text-right tabular-nums">{r.connected}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              {talk(r.talkSeconds)}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>

                {unmapped.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
                    <p className="text-sm max-w-2xl">
                      {unmapped.length} phone account{unmapped.length === 1 ? " isn't" : "s aren't"}{' '}
                      set up yet. You can import now: those calls come in without a rep, and GRM
                      updates them as soon as you say who each account belongs to, here or under
                      Phone accounts.
                    </p>
                    <Button
                      variant="secondary"
                      disabled={!anyChosen || saveAccounts.isPending}
                      onClick={() =>
                        saveAccounts.mutate(
                          unmapped
                            .filter((a) => choices[a] && choices[a] !== NO_USER)
                            .map((a) => choiceFor(a, choices[a] ?? NO_USER))
                        )
                      }
                    >
                      {saveAccounts.isPending ? 'Saving…' : 'Save phone accounts'}
                    </Button>
                  </div>
                )}

                <Button
                  onClick={() => file && commitMutation.mutate(file)}
                  disabled={!preview.totals.new || busy}
                >
                  {commitMutation.isPending
                    ? 'Importing…'
                    : preview.totals.new
                      ? `Import ${preview.totals.new} call${preview.totals.new === 1 ? '' : 's'}`
                      : 'Nothing new to import'}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ---------------- Not matched ---------------- */}
        <TabsContent value="unmatched" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Calls not matched to GRM</CardTitle>
              <CardDescription>
                The other party's number isn&apos;t on any contact, lead or account, or it&apos;s on
                several different accounts. Add the number to the right record, then match again.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                variant="secondary"
                onClick={() => rematchMutation.mutate()}
                disabled={rematchMutation.isPending || !unmatchedTotal}
              >
                {rematchMutation.isPending ? 'Matching…' : 'Match again'}
              </Button>
              {unmatchedQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : unmatchedQuery.isError ? (
                <p className="text-sm text-destructive">
                  {explainError(unmatchedQuery.error, "The unmatched calls couldn't be loaded.")}
                </p>
              ) : unmatchedTotal === 0 ? (
                <p className="text-sm text-muted-foreground">Every imported call is matched.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">Number</TableHead>
                        <TableHead scope="col">Rep</TableHead>
                        <TableHead scope="col">When</TableHead>
                        <TableHead scope="col">Outcome</TableHead>
                        <TableHead scope="col">Why</TableHead>
                        <TableHead scope="col">
                          <span className="sr-only">Search</span>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(unmatchedQuery.data?.data ?? []).map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-mono text-sm">
                            {c.formatted_phone || c.phone_number}
                          </TableCell>
                          <TableCell>{c.owner_name ?? '–'}</TableCell>
                          <TableCell>{formatDateTime(c.started_at)}</TableCell>
                          <TableCell>
                            {c.disposition}, {talk(c.duration_seconds)}
                          </TableCell>
                          <TableCell>
                            {c.match_status === 'ambiguous' ? 'On several accounts' : 'Not in GRM'}
                          </TableCell>
                          <TableCell>
                            <Link
                              to={`/search?q=${encodeURIComponent(c.phone_number.slice(-7))}`}
                              className="text-sm underline"
                            >
                              Search GRM
                              <span className="sr-only">
                                {' '}
                                for {c.formatted_phone || c.phone_number}
                              </span>
                            </Link>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------------- Phone accounts ---------------- */}
        <TabsContent value="accounts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Phone accounts</CardTitle>
              <CardDescription>
                GRM uses the VoIP.ms sub-account to tell which rep made or took a call. Accounts
                that appear on imported calls are listed here; say who each belongs to, or mark a
                shared line as not a rep. Changing an account also updates its past imported calls.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {accountsQuery.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading…</p>
              ) : accountsQuery.isError ? (
                <p className="text-sm text-destructive">
                  {explainError(accountsQuery.error, "The phone accounts couldn't be loaded.")}
                </p>
              ) : !accountsQuery.data?.length ? (
                <p className="text-sm text-muted-foreground">
                  None yet. Import some calls and the phone accounts on them will appear here.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">Phone account</TableHead>
                        <TableHead scope="col">Belongs to</TableHead>
                        <TableHead scope="col" className="text-right">
                          Calls imported
                        </TableHead>
                        <TableHead scope="col">Last call</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accountsQuery.data.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell className="font-mono text-sm">
                            {a.external_account}
                            {!a.mapped && (
                              <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-sans text-xs text-muted-foreground">
                                not set
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <AccountPicker
                              id={`acct-${a.external_account}`}
                              account={a.external_account}
                              value={!a.mapped ? NO_USER : a.user_id ? a.user_id : NOT_A_REP}
                              onChange={(v) =>
                                saveAccounts.mutate([choiceFor(a.external_account, v)])
                              }
                              users={users}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{a.calls}</TableCell>
                          <TableCell>{a.last_call ? formatDateTime(a.last_call) : '–'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </main>
  )
}
